<template>
    <div class="schedule-print-container">
        <DotsLoading v-if="loading" />
        <div v-else>
            <div class="actions">
                <a class="button" :href="mailto">{{ t('send_email') }}</a>
                <button type="button" class="button" @click="print">{{ t('print') }}</button>
            </div>
            <div class="schedule-print">
                <PrintableSchedulePrint
                    :period="period"
                    :route="route"
                    :direction="direction"
                    :stops="stops"
                    :days="days"
                    :options="options"
                />
            </div>
        </div>
    </div>
</template>

<script setup>
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { ref, computed, onMounted } from 'vue';
import groupBy from 'lodash/groupBy';
import uniqWith from 'lodash/uniqWith';

import { addDays, format } from '@@/js/utils/dateFns';
import { constants } from '@@/js/utils/constants';
import { useEffectiveDate } from '@/schedules/composables/useEffectiveDate';
import { useSchedulesStore } from '@@/js/stores/schedules';
import DotsLoading from '@/shared/DotsLoading';
import PrintableSchedulePrint from '@/printable-schedule/components/PrintableSchedulePrint';

const { t } = useI18n({
    messages: {
        fr: {
            send_email: 'Envoyer par courriel',
            print: 'Imprimer',
            email_subject: 'RTC - Votre horaire sur mesure - Parcours {route}',
            email_template: "Bonjour%2C%0D%0A%0D%0APour consulter votre horaire sur mesure%2C cliquez sur le lien suivant :%0D%0A{url}%0D%0A%0D%0ACe lien restera actif jusqu'au {expiry}%0D%0AAprès cette date%2C votre horaire sera expiré et ne pourra plus être consulté.%0D%0A%0D%0APour créer un nouvel horaire%2C cliquez sur le lien suivant :%0D%0A{generator}%0D%0A%0D%0AMerci d'utiliser nos services%2C allez-y pour le bus!"
        },
        en: {
            send_email: 'Send by email',
            print: 'Print',
            email_subject: 'RTC: Your Personalized Schedule, Route {route}',
            email_template: "Dear Rider%2C%0D%0A%0D%0AClick the following link to check your personalized schedule:%0D%0A{url}%0D%0A%0D%0ALink active until {expiry}%0D%0AYour schedule expires on that date and will no longer be available.%0D%0A%0D%0ATo make a new schedule%2C click the following link:%0D%0A{generator}%0D%0A%0D%0AThank you for using our services - Get on the bus!",
        },
    },
});

const schedulesStore = useSchedulesStore();
const { getApplicationPeriods, fetchRoute, getDirectionDetails, fetchStops, fetchDepartures } = schedulesStore;

const vRoute = useRoute();
const { params, fullPath, query } = vRoute;
const router = useRouter();

const { getEffectiveDate } = useEffectiveDate();

const loading = ref(true);
const period = ref();
const route = ref();
const direction = ref();
const stops = ref();
const days = ref();
const options = ref();

const mailto = computed(() => {
    // @TODO: Gestion des vraies url, pour l'instant on a seulement accès au fullPath fourni par vue-router
    const subject = t('email_subject', { route: params.route });
    const url = encodeURIComponent((constants.BASE_URL + fullPath).replace(/([^:]\/)\/+/g, '$1'));
    const generator = encodeURIComponent(
        (constants.BASE_URL + router.resolve({ name: 'printable-schedule' }).href).replace(
            /([^:]\/)\/+/g,
            '$1',
        ),
    );
    const expiry = getEffectiveDate(period.value.dateFinPeriode);
    const body = t('email_template', {
        url,
        generator,
        expiry,
    });
    return `mailto:?subject=${encodeURIComponent(subject)}&body=${body}`;
});

onMounted(async () => {
    if (params.route) {
        const { a: selectedStops, sc: schedule, ta: allDay, tf: from, tt: to, s: size, t: line, p: index } = query;

        const periods = await getApplicationPeriods();
        const indexPeriod = index || 0;
        period.value = periods[indexPeriod];

        let effectiveDate = new Date();
        if (periods[indexPeriod] && new Date(periods[indexPeriod].dateDebutPeriode) > effectiveDate) {
            effectiveDate = new Date(periods[indexPeriod].dateDebutPeriode);
        }

        route.value = await fetchRoute(params.route, effectiveDate);
        direction.value = getDirectionDetails(route.value, params.direction);
        stops.value = await fetchStops(route.value.noParcours, direction.value.code, effectiveDate);
        stops.value = stops.value.filter((a) => selectedStops.includes(a.noArret));
        const stopsIds = stops.value.map((a) => a.noArret);
        days.value = [];

        let possibleDates = [];
        let startDay = new Date();
        if (indexPeriod > 0) {
            startDay = new Date(period.value.dateDebutPeriode);
        }
        startDay.setHours(0, 0, 0, 0);
        for (let i = 0; i < 14; i++) {
            possibleDates.push(addDays(startDay, i));
        }
        possibleDates = possibleDates
            .filter((date) => {
                return route.value.jours.split('')[date.getDay()] === '1';
            })
            .filter((date) => {
                return date <= new Date(periods[indexPeriod].dateFinPeriode);
            });
        for (const day of [].concat(schedule)) {
            let filteredDates = [];
            const extraDates = [];
            let departures = [];

            possibleDates = possibleDates.filter((date) => !extraDates.includes(date));

            switch (day) {
                case 'sunday':
                    filteredDates = possibleDates.filter((date) => {
                        return date.getDay() === 0;
                    });
                    break;
                case 'weekdays':
                    if (params.route.toLowerCase() === '92a') {
                        extraDates.push(
                            possibleDates.find((date) => {
                                return date.getDay() === 5;
                            }),
                        );
                        filteredDates = possibleDates.filter((date) => {
                            return date.getDay() > 0 && date.getDay() < 5;
                        });
                    } else if (params.route.toLowerCase() === '32') {
                        extraDates.push(
                            possibleDates.find((date) => {
                                return date.getDay() === 4;
                            }),
                            possibleDates.find((date) => {
                                return date.getDay() === 5;
                            }),
                        );
                        filteredDates = possibleDates.filter((date) => {
                            return date.getDay() > 0 && date.getDay() < 4;
                        });
                    } else {
                        filteredDates = possibleDates.filter((date) => {
                            return date.getDay() !== 0 && date.getDay() !== 6;
                        });
                    }
                    break;
                case 'saturday':
                    filteredDates = possibleDates.filter((date) => {
                        return date.getDay() === 6;
                    });
                    break;
            }
            const singleDepartures = [];
            for (const noStop of [].concat(stopsIds)) {
                let stopDepartures = [];
                if (filteredDates.length > 0) {
                    stopDepartures = await fetchDepartures(
                        params.route,
                        noStop,
                        direction.value.code,
                        filteredDates[0],
                    );
                }
                stopDepartures = stopDepartures.map((p) => {
                    return {
                        ...p,
                        noStop,
                    };
                });
                departures.push(...stopDepartures);
                singleDepartures.push(...stopDepartures);

                if (extraDates && extraDates.length > 0) {
                    for (const extraDate of [].concat(extraDates)) {
                        stopDepartures = await fetchDepartures(
                            params.route,
                            noStop,
                            direction.value.code,
                            extraDate,
                        );
                        stopDepartures = stopDepartures.map((p) => {
                            return {
                                ...p,
                                noStop,
                                extraDate,
                            };
                        });
                        departures.push(...stopDepartures);
                    }
                }
            }
            departures = uniqWith(departures, (a, b) => {
                return (
                    a.noStop === b.noStop &&
                    format(a.depart, 'HH:mm') === format(b.depart, 'HH:mm')
                );
            });
            const groupedDepartures = groupBy(departures, 'idVoyage');
            const groupedDeparturesArray = [];
            Object.values(groupedDepartures).forEach((value) => {
                groupedDeparturesArray.push(value);
            });
            groupedDeparturesArray.sort((a, b) => {
                return new Date(a[0].depart) - new Date(b[0].depart);
            });
            let filteredDepartures = groupedDeparturesArray;
            if (allDay === 'false') {
                const currentDate = format(filteredDates[0], 'yyyy-MM-dd');
                let fromDateTime = new Date(`${currentDate} ${from}`);
                let toDateTime = new Date(`${currentDate} ${to}`);
                if (parseInt(from.substring(0, 2)) <= 4) {
                    fromDateTime = addDays(fromDateTime, 1);
                }
                if (parseInt(to.substring(0, 2)) <= 4) {
                    toDateTime = addDays(toDateTime, 1);
                }
                filteredDepartures = filteredDepartures.filter((departures) => {
                    return departures.some((passage) => {
                        const time = new Date(passage.depart);
                        return time > fromDateTime && time < toDateTime;
                    });
                });
            }

            days.value.push({
                type: day,
                passages: filteredDepartures,
            });
            days.value.sort((a, b) => {
                const sorting = ['weekdays', 'saturday', 'sunday'];
                return sorting.indexOf(a.type) - sorting.indexOf(b.type);
            });
        }
        options.value = {
            stopsIds: stopsIds,
            selectedStops: selectedStops,
            schedule: schedule,
            allDay: allDay === 'true',
            from: from,
            to: to,
            size: size,
            line: line === 'true',
        };
    }
    loading.value = false;
});

function print() {
    window.print();
}
</script>

<style lang="scss" scoped>
.schedule-print-container,
.schedule-print-container * {
    visibility: visible;
}
.schedule-print-container {
    position: absolute;
    left: 50%;
    top: 0;
    transform: translateX(-50%);
    padding: 24px 16px;
    width: 100%;
    @media print {
        padding: 0;
    }
}
.actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-bottom: 24px;
    text-align: right;
    @media print {
        display: none;
    }
}
.schedule-print {
    background-color: $color-white;
    padding: 16px;
    border-radius: 20px;
    @include breakpoint(medium) {
        padding: 24px;
    }
    @media print {
        padding: 0;
    }
}
</style>

<style lang="scss">
body.print-page * {
    visibility: hidden;
}
</style>
